// Iter 82.3 / Phase E1.3 — Leave Ledger Report.
// Reporting view of the leave ledger with CSV + XLSX exports.
// Operational drill-down view remains at /admin/leave/ledger.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FunnelSimple, FileXls, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const MOVEMENT_TYPES = [
    "opening_balance", "accrual", "manual_adjustment",
    "leave_taken", "leave_cancelled", "carry_forward",
    "encashment", "expiry", "lop_charge",
];

const MOVEMENT_TONE = {
    opening_balance: "bg-slate-100 text-slate-700",
    accrual: "bg-emerald-100 text-emerald-700",
    manual_adjustment: "bg-amber-100 text-amber-700",
    leave_taken: "bg-rose-100 text-rose-700",
    leave_cancelled: "bg-sky-100 text-sky-700",
    carry_forward: "bg-indigo-100 text-indigo-700",
    encashment: "bg-violet-100 text-violet-700",
    expiry: "bg-orange-100 text-orange-700",
    lop_charge: "bg-red-100 text-red-700",
};

const EMPTY_FILTERS = {
    employee_id: "", department: "", leave_type_code: "",
    fiscal_year: "", movement_type: "", period: "",
    from_date: "", to_date: "",
};

export default function LeaveLedgerReport() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
    const [loading, setLoading] = useState(true);
    const [fys, setFys] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [] });

    const loadMeta = useCallback(async () => {
        try {
            const [fyResp, hr] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/hr/employee/meta"),
            ]);
            setFys(fyResp.data.items || []);
            setHrMeta(hr.data);
        } catch (err) { toast.error(formatApiError(err, "Failed to load meta")); }
    }, []);

    const reload = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = { page, page_size: 50 };
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/leave/ledger/list", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load ledger"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(1); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const path = ext === "xlsx" ? "/leave/report/ledger/export.xlsx" : "/leave/ledger/export";
            const { data: blob } = await api.get(path, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-ledger-${new Date().toISOString().slice(0, 10)}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    const activeCount = Object.values(filters).filter(Boolean).length;

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-ledger-report-page">
            <PageHeader
                eyebrow="Leave Reports"
                title="Leave Ledger Report"
                description="Append-only audit log of every leave balance movement. For deeper operational drill-down, use the admin ledger explorer."
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="lr-export-csv">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="lr-export-xlsx">
                            <FileXls size={14} className="mr-1" /> Excel
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="lr-filters">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <FunnelSimple size={14} /> Filters
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)} disabled={activeCount === 0} data-testid="lr-clear-btn">
                        <X size={12} className="mr-1" /> Clear Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                    </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Employee ID</label>
                        <Input data-testid="lr-emp-id" placeholder="EMP-…" value={filters.employee_id}
                            onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select data-testid="lr-dept" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>
                            <option value="">All</option>
                            {(hrMeta.departments || []).map((d) => (<option key={d.id || d.name} value={d.name}>{d.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <Input data-testid="lr-type" placeholder="CL / SL / EL" value={filters.leave_type_code}
                            onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value.toUpperCase() })} className="mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Fiscal Year</label>
                        <select data-testid="lr-fy" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.fiscal_year} onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}>
                            <option value="">All</option>
                            {fys.map((fy) => (<option key={fy.id} value={fy.code}>{fy.code}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Movement Type</label>
                        <select data-testid="lr-movement" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.movement_type} onChange={(e) => setFilters({ ...filters, movement_type: e.target.value })}>
                            <option value="">All</option>
                            {MOVEMENT_TYPES.map((m) => (<option key={m} value={m}>{m.replace(/_/g, " ")}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Period (YYYY-MM)</label>
                        <Input data-testid="lr-period" placeholder="2026-04" value={filters.period}
                            onChange={(e) => setFilters({ ...filters, period: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">From</label>
                        <Input type="date" data-testid="lr-from" value={filters.from_date}
                            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">To</label>
                        <Input type="date" data-testid="lr-to" value={filters.to_date}
                            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="mt-1" />
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="lr-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Movement</th>
                                <th className="text-right px-3 py-2 font-medium">Delta</th>
                                <th className="text-right px-3 py-2 font-medium">Balance After</th>
                                <th className="text-left px-3 py-2 font-medium">Reference</th>
                                <th className="text-left px-3 py-2 font-medium">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-400">Loading…</td></tr>}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No ledger entries match.</td></tr>
                            )}
                            {!loading && data.items.map((r) => {
                                const emp = r.employee || {};
                                return (
                                    <tr key={r.id} className="border-t border-slate-100" data-testid={`lr-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-900">{emp.full_name || r.employee_id}</div>
                                            <div className="text-xs text-slate-500 font-mono">{emp.employee_code || r.employee_id}</div>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                        <td className="px-3 py-2">
                                            <Badge className={`text-xs ${MOVEMENT_TONE[r.movement_type] || "bg-slate-100"}`}>{r.movement_type}</Badge>
                                        </td>
                                        <td className={`px-3 py-2 text-right font-mono ${r.delta_days < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                            {r.delta_days > 0 ? "+" : ""}{Number(r.delta_days).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold">{Number(r.balance_after).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.reference_id || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(r.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {data.pages > 1 && (
                <div className="mt-3 flex justify-between text-xs text-slate-500" data-testid="lr-pager">
                    <div>Page {data.page} of {data.pages} · {data.total} entries</div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => reload(data.page - 1)} data-testid="lr-prev">Prev</Button>
                        <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => reload(data.page + 1)} data-testid="lr-next">Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
